import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useClaimDelete } from 'all-in-one-shared-api';

import { MessageType } from 'models/MessageType';
import { ClaimsPageState } from 'models';

import ClaimDetailsModal, { ClaimDetailsModalProps } from '../ClaimDetailsModal';

interface DeleteClaimModalProps extends Pick<ClaimDetailsModalProps, 'visible' | 'setVisibility'> {
  referenceNo: string;
  authToken: string;
  onSuccess?: () => void;
  onFail?: () => void;
}

export const DeleteClaimModal = ({
  visible,
  setVisibility,
  referenceNo,
  authToken,
  onSuccess,
  onFail,
}: DeleteClaimModalProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const { data, error, shouldFetch, isLoading } = useClaimDelete(
    {
      name: 'useClaimDelete',
      params: { referenceNo },
    },
    authToken,
    referenceNo,
  );

  useEffect(() => {
    const onDone = () => {
      shouldFetch(false);
      setVisibility(false);
    };

    if (typeof data === 'string') {
      onDone();
      onSuccess?.();
      navigate('/claims', { state: { type: ClaimsPageState.CLAIM_DELETED, value: referenceNo } });
    }

    if (error) {
      onDone();
      onFail?.();
      if (error.code === 'dapi.claim.not.allow.operate') {
        setShowModal(true);
      } else {
        message[MessageType.ERROR](t('claimDetails.deleteClaimFailure', { referenceNo }));
      }
    }
  }, [data, error, navigate, onFail, onSuccess, referenceNo, setVisibility, shouldFetch, t]);

  const handleDelete = () => {
    shouldFetch(true);
  };

  return (
    <>
      <ClaimDetailsModal
        handleOk={handleDelete}
        visible={visible}
        setVisibility={setVisibility}
        modalLoading={isLoading}
        title={t('claimDetails.areYouSureToDeleteThisClaim')}
        description={t('claimDetails.deleteModalBody')}
      />
      <ClaimDetailsModal
        handleOk={() => setShowModal(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        visible={showModal}
        setVisibility={setShowModal}
        modalLoading={false}
        title={t('claimDetails.deleteErrorTitle')}
        description={t('claimDetails.deleteError')}
      />
    </>
  );
};
